/**
 * LES SURVEILLANCES DE L'ENTREPRISE ET LEURS ALARMES OUVERTES.
 * ============================================================================
 *
 * INTENTION : la liste montre toutes les surveillances que le rôle de la
 * personne lui laisse lire, chacune avec son alarme en cours.
 *
 * Deux faits de la façade décident de la lecture :
 *  • une page vide avec un `curseur_suivant` non nul n'est PAS une fin : le
 *    service filtre le droit de lire APRÈS sa limite (api-metier
 *    `instance.get.route.js`, « Contrat »). Le curseur se suit donc jusqu'à
 *    `null`, au plus `PLAFOND_PAGES_VIDES` pages vides de suite ; au-delà, la
 *    lecture s'arrête et l'écran propose « Afficher la suite » ;
 *  • les alarmes ouvertes se lisent par la liste (`ouverte=true`), jamais par le
 *    détail : ouvrir le détail d'une alarme revenue à la normale la clôt (D71).
 *
 * Tenu par entreprise, comme la lecture de l'ouverture : une autre entreprise
 * vide tout tout de suite. Chaque lecture porte son numéro : toute fin de
 * lecture (page, succès, échec) qui n'est plus celle de la lecture courante,
 * ou dont la session (personne et entreprise) a changé, est jetée sans
 * toucher à l'état. Sans quoi l'échec tardif d'une lecture de A viderait la
 * liste de B, ou la laisserait « prête » et amputée : un faux « aucune
 * surveillance ».
 *
 * Un écran de surveillance ne sert pas une lecture ancienne : la page demande
 * `charger({ relire: true })` à chaque entrée, et la liste est relue même pour
 * la même entreprise. Seule une lecture déjà en vol est partagée.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    versErreurAlarmesV2,
    ErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** La taille d'une page demandée, le maximum de la façade. */
export const LIMITE_PAGE = 200

/** Le nombre de pages vides de suite au-delà duquel la lecture s'arrête. [PROPOSITION] */
export const PLAFOND_PAGES_VIDES = 5

/** Le plafond de pages d'alarmes ouvertes, garde contre une boucle sans fin. */
const PLAFOND_PAGES_ALARMES = 50

/** Où en est la lecture. */
export type StatutListe = "attente" | "chargement" | "pret" | "erreur"

/** Levée quand l'entreprise a changé en cours de lecture. */
class LectureObsolete extends Error {}

/**
 * Ce qui n'a pas pu être lu en entier : une page sans liste, une alarme sans
 * sa surveillance, des alarmes arrêtées au plafond. La liste échoue plutôt que
 * de se dire vide ou « En service » sur ce qu'elle n'a pas lu.
 */
const illisible = () =>
    new ErreurAlarmesV2({ code: CODE_INDISPONIBLE, statut: null })

export const useSurveillancesStore = defineStore(
    "alarmesV2Surveillances",
    () => {
        /** Les surveillances lues, dans l'ordre du service. */
        const surveillances = ref<AlarmesV2Surveillance[]>([])

        /** L'alarme ouverte de chaque surveillance, par `_id_instance`. */
        const alarmesParInstance = ref<Record<string, AlarmesV2Alarme>>({})

        /** Les noms des personnes de l'entreprise, servis avec les alarmes. */
        const personnes = ref<Record<string, AlarmesV2Personne>>({})

        /** Où en est la lecture. */
        const statut = ref<StatutListe>("attente")

        /** L'échec de la dernière lecture, `null` sinon. */
        const erreur = ref<ErreurAlarmesV2 | null>(null)

        /** Vrai quand la lecture s'est arrêtée au plafond de pages vides. */
        const suiteDisponible = ref(false)

        /** L'entreprise lue. */
        const societeLue = ref("")

        /** La clé de session de la lecture (`cleDeSession`) : la personne et l'entreprise. */
        let cleLue = ""

        /** Le curseur où reprendre la lecture des surveillances. */
        let curseurSuite: string | null = null

        /** La lecture en vol, partagée, et son entreprise. */
        let enVol: Promise<void> | null = null
        let enVolPour = ""

        /** Le numéro de la lecture courante ; une lecture d'un autre numéro est jetée. */
        let lectureCourante = 0

        /** Le numéro de la relecture des alarmes courante ; une plus ancienne est jetée. */
        let relectureCourante = 0

        /** Vide tout, sans rien lire. */
        function vider(): void {
            surveillances.value = []
            alarmesParInstance.value = {}
            personnes.value = {}
            erreur.value = null
            suiteDisponible.value = false
            curseurSuite = null
        }

        /** Vrai quand la lecture `numero` de la session `societe` est toujours la courante. */
        function estCourante(societe: string, numero: number): boolean {
            return numero === lectureCourante && cleDeSession() === societe
        }

        /** Lève `LectureObsolete` si la lecture n'est plus la courante. */
        function verifier(societe: string, numero: number): void {
            if (!estCourante(societe, numero)) throw new LectureObsolete()
        }

        /**
         * Lit les surveillances depuis `curseur` jusqu'à `null`, ou jusqu'au
         * plafond de pages vides de suite.
         */
        async function lireLesSurveillances(
            societe: string,
            numero: number,
            curseur: string | null
        ): Promise<void> {
            let suivant = curseur
            let videsDeSuite = 0
            do {
                const page =
                    await serviceAlarmesV2.alarmesV2ListerSurveillances({
                        limite: LIMITE_PAGE,
                        ...(suivant ? { curseur: suivant } : {}),
                    })
                verifier(societe, numero)
                if (!Array.isArray(page?.items)) throw illisible()
                const items = page.items
                surveillances.value = [...surveillances.value, ...items]
                videsDeSuite = items.length === 0 ? videsDeSuite + 1 : 0
                suivant = page?.curseur_suivant ?? null
            } while (suivant && videsDeSuite < PLAFOND_PAGES_VIDES)
            curseurSuite = suivant
            suiteDisponible.value = suivant !== null
        }

        /** Lit toutes les alarmes ouvertes de l'entreprise, page après page. */
        async function lireLesAlarmesOuvertes(
            societe: string,
            numero: number
        ): Promise<void> {
            const lu = await lireLesAlarmes(() => verifier(societe, numero))
            alarmesParInstance.value = lu.parInstance
            personnes.value = lu.noms
        }

        /**
         * Lit les alarmes ouvertes sans rien poser ; `verif` lève si la
         * lecture n'est plus la courante.
         */
        async function lireLesAlarmes(verif: () => void): Promise<{
            parInstance: Record<string, AlarmesV2Alarme>
            noms: Record<string, AlarmesV2Personne>
        }> {
            const parInstance: Record<string, AlarmesV2Alarme> = {}
            const noms: Record<string, AlarmesV2Personne> = {}
            let suivant: string | null = null
            let pages = 0
            do {
                const page = await serviceAlarmesV2.alarmesV2ListerAlarmes({
                    ouverte: true,
                    limite: LIMITE_PAGE,
                    ...(suivant ? { curseur: suivant } : {}),
                })
                verif()
                if (!Array.isArray(page?.items)) throw illisible()
                for (const alarme of page.items) {
                    // Une alarme ouverte qu'on ne sait pas rattacher laisserait
                    // sa surveillance « En service » : la liste échoue.
                    if (
                        typeof alarme?._id_instance !== "string" ||
                        alarme._id_instance === ""
                    )
                        throw illisible()
                    // La première alarme ouverte (la plus ancienne) fait foi.
                    if (!parInstance[alarme._id_instance]) {
                        parInstance[alarme._id_instance] = alarme
                    }
                }
                Object.assign(noms, page?.personnes ?? {})
                suivant = page?.curseur_suivant ?? null
                pages += 1
            } while (suivant && pages < PLAFOND_PAGES_ALARMES)
            // Arrêtée au plafond : des alarmes ouvertes restent non lues.
            if (suivant) throw illisible()
            return { parInstance, noms }
        }

        /**
         * Lance une lecture et range son issue, sans jamais rejeter. Un échec des
         * alarmes ouvertes fait échouer toute la liste : une surveillance montrée
         * « En service » alors qu'une alarme y est ouverte serait un faux calme.
         * Une issue qui n'appartient plus à la lecture courante est jetée.
         */
        async function lancer(
            societe: string,
            lecture: (numero: number) => Promise<void>
        ): Promise<void> {
            lectureCourante += 1
            const numero = lectureCourante
            statut.value = "chargement"
            const enCours = (async () => {
                try {
                    await lecture(numero)
                    if (!estCourante(societe, numero)) return
                    statut.value = "pret"
                } catch (e) {
                    if (!estCourante(societe, numero)) return
                    vider()
                    erreur.value =
                        e instanceof ErreurAlarmesV2
                            ? e
                            : versErreurAlarmesV2(e)
                    statut.value = "erreur"
                }
                cleLue = societe
                societeLue.value = useAuthStore().companyId
            })()
            enVol = enCours
            try {
                await enCours
            } finally {
                if (enVol === enCours) enVol = null
            }
        }

        /**
         * Lit la liste de l'entreprise courante. Ne rejette jamais.
         * @param options.relire - relire même une entreprise déjà lue (entrée sur la page)
         */
        async function charger(
            options: { relire?: boolean } = {}
        ): Promise<void> {
            const societe = cleDeSession()
            if (!societe) {
                reinitialiser()
                return
            }
            if (!options.relire && cleLue === societe) return
            if (enVol && statut.value === "chargement" && enVolPour === societe)
                return enVol
            vider()
            enVolPour = societe
            return lancer(societe, async (numero) => {
                await lireLesSurveillances(societe, numero, null)
                await lireLesAlarmesOuvertes(societe, numero)
            })
        }

        /** Reprend la lecture des surveillances après le plafond de pages vides. */
        async function afficherLaSuite(): Promise<void> {
            const societe = cleDeSession()
            if (!curseurSuite || societe !== cleLue) return
            const depuis = curseurSuite
            suiteDisponible.value = false
            return lancer(societe, async (numero) => {
                await lireLesSurveillances(societe, numero, depuis)
                await lireLesAlarmesOuvertes(societe, numero)
            })
        }

        /**
         * Relit les alarmes ouvertes d'une liste déjà lue, après un
         * acquittement, SANS la vider : les surveillances, leur ordre et
         * « Afficher la suite » restent ; seules les alarmes (et leurs noms)
         * sont remplacées, d'un coup, quand la relecture a abouti. Un échec
         * ne touche à rien et rend faux : l'écran garde la liste et dit
         * « non relu ». Rend vrai si la relecture a abouti.
         */
        async function relireLesAlarmes(): Promise<boolean> {
            const societe = cleDeSession()
            if (!societe || societe !== cleLue || statut.value !== "pret")
                return false
            const numero = lectureCourante
            relectureCourante += 1
            const relecture = relectureCourante
            const encore = () =>
                estCourante(societe, numero) &&
                relecture === relectureCourante &&
                statut.value === "pret"
            try {
                const lu = await lireLesAlarmes(() => {
                    if (!encore()) throw new LectureObsolete()
                })
                if (!encore()) return false
                alarmesParInstance.value = lu.parInstance
                personnes.value = lu.noms
                return true
            } catch {
                return false
            }
        }

        /**
         * L'alarme ouverte d'une surveillance, ou `null`.
         * @param idSurveillance - `_id` de la surveillance
         */
        function alarmeDe(idSurveillance: string): AlarmesV2Alarme | null {
            return alarmesParInstance.value[idSurveillance] ?? null
        }

        /** Remet le store à son état de départ. */
        function reinitialiser(): void {
            vider()
            lectureCourante += 1
            statut.value = "attente"
            societeLue.value = ""
            cleLue = ""
            enVol = null
            enVolPour = ""
        }

        return {
            surveillances,
            personnes,
            statut,
            erreur,
            suiteDisponible,
            societeLue,
            charger,
            afficherLaSuite,
            relireLesAlarmes,
            alarmeDe,
            reinitialiser,
        }
    }
)
